import { create } from 'zustand';
import { broadcastApi } from '../api';

const useBroadcastStore = create((set, get) => ({
  broadcasts: [],
  currentBroadcast: null,
  history: [],
  schedules: [],
  drafts: [],
  isLoading: false,
  error: null,

  setBroadcasts: (broadcasts) => set({ broadcasts }),

  setCurrentBroadcast: (broadcast) => set({ currentBroadcast: broadcast }),

  fetchBroadcasts: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.getAll(params);
      set({ broadcasts: response.data.data || response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch broadcasts', isLoading: false });
      throw error;
    }
  },

  fetchBroadcastById: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.getById(id);
      set({ currentBroadcast: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch broadcast', isLoading: false });
      throw error;
    }
  },

  createBroadcast: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.create(data);
      set((state) => ({
        broadcasts: [...state.broadcasts, response.data],
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to create broadcast', isLoading: false });
      throw error;
    }
  },

  sendBroadcast: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.send({ campaignId: id });
      set((state) => ({
        broadcasts: state.broadcasts.map(b => b.id === id ? { ...b, status: 'sent' } : b),
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to send broadcast', isLoading: false });
      throw error;
    }
  },

  scheduleBroadcast: async (id, scheduleData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.schedule({ campaignId: id, ...(scheduleData || {}) });
      set((state) => ({
        broadcasts: state.broadcasts.map(b => b.id === id ? { ...b, scheduled: true, scheduleData } : b),
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to schedule broadcast', isLoading: false });
      throw error;
    }
  },

  cancelSchedule: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.cancelSchedule({ campaignId: id });
      set((state) => ({
        broadcasts: state.broadcasts.map(b => b.id === id ? { ...b, scheduled: false } : b),
        schedules: state.schedules.filter(s => s.id !== id),
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to cancel schedule', isLoading: false });
      throw error;
    }
  },

  fetchHistory: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.getHistory(params);
      set({ history: response.data.data || response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch history', isLoading: false });
      throw error;
    }
  },

  fetchScheduleHistory: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.getScheduleHistory(params);
      set({ schedules: response.data.data || response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch schedule history', isLoading: false });
      throw error;
    }
  },

  fetchDrafts: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.getDrafts(params);
      set({ drafts: response.data.data || response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch drafts', isLoading: false });
      throw error;
    }
  },

  saveDraft: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await broadcastApi.saveDraft(data);
      set((state) => ({
        drafts: [...state.drafts, response.data],
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to save draft', isLoading: false });
      throw error;
    }
  },

  deleteDraft: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await broadcastApi.deleteDraft(id);
      set((state) => ({
        drafts: state.drafts.filter(d => d.id !== id),
        isLoading: false
      }));
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to delete draft', isLoading: false });
      throw error;
    }
  },

  deleteBroadcast: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await broadcastApi.delete(id);
      set((state) => ({
        broadcasts: state.broadcasts.filter(b => b.id !== id),
        isLoading: false
      }));
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to delete broadcast', isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    broadcasts: [],
    currentBroadcast: null,
    history: [],
    schedules: [],
    drafts: [],
    isLoading: false,
    error: null
  }),
}));

export default useBroadcastStore;
